// src/modules/perfil/PerfilTurmasDashboard.js
import React, { useState, useEffect, useMemo } from 'react';
import { db, paths } from '../../firebase/config';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList
} from 'recharts';

// --- CONFIGURAÇÕES VISUAIS: PALETA DISTINTA E VIBRANTE ---
const COLORS_GENDER = ['#ec4899', '#3b82f6']; 
const COLORS_ORIGIN = ['#f59e0b', '#1e293b']; 
const COLORS_AGE_PIE = ['#0ea5e9', '#10b981', '#eab308', '#ef4444', '#8b5cf6', '#64748b']; 
const BAR_COLOR_AGE = '#6366f1'; 
const BAR_COLOR_INGRESSO = '#14b8a6'; 

// --- FUNÇÕES AUXILIARES ---

const normalizeText = (text) => {
    if (!text) return "";
    return text.toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase().trim();
};

const getYear = (dateString) => {
    if (!dateString) return null;
    try {
        const cleanDate = dateString.toString().replace(/\s/g, '');
        if (cleanDate.includes('-')) return parseInt(cleanDate.split('-')[0]);
        if (cleanDate.includes('/')) return parseInt(cleanDate.split('/')[2]);
        const dateObj = new Date(cleanDate);
        if (!isNaN(dateObj.getFullYear())) return dateObj.getFullYear();
        return null;
    } catch { return null; }
};

// Tooltip para exibir a quantidade exata no hover
const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const color = payload[0].payload?.fill || payload[0].fill || '#94a3b8';
      return (
        <div className="bg-white/95 backdrop-blur-sm p-3 border border-slate-100 rounded-xl shadow-lg text-sm z-50 text-slate-700 min-w-[120px]">
          <p className="font-semibold text-slate-900 mb-1">{label || payload[0].name}</p>
          <div className="flex items-center gap-2">
             <div 
                className="w-2.5 h-2.5 rounded-full" 
                style={{ backgroundColor: color }}
             ></div>
             <p className="text-slate-600">Qtd: <span className="font-semibold text-slate-900">{payload[0].value}</span></p>
          </div>
        </div>
      );
    }
    return null;
};

// Função para renderizar a porcentagem dentro da fatia do gráfico
const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    // Se a fatia for menor que 5%, não exibe o texto para não ficar sobreposto/vazando
    if (percent < 0.05) return null;
    
    const RADIAN = Math.PI / 180;
    // Calcula o raio médio para posicionar o texto bem no centro da fatia
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
        <text 
            x={x} 
            y={y} 
            fill="white" 
            textAnchor="middle" 
            dominantBaseline="central" 
            fontSize={13} 
            fontWeight="bold"
            style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }} // Sombreado leve para destacar no fundo claro
        >
            {`${(percent * 100).toFixed(0)}%`}
        </text>
    );
};

export const PerfilTurmasDashboard = () => {
  const [cadetes, setCadetes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mapaTurmas, setMapaTurmas] = useState({});
  const [turmasDisponiveis, setTurmasDisponiveis] = useState([]);
  const [turmaSelecionadaId, setTurmaSelecionadaId] = useState('');

  useEffect(() => {
    const fetchDados = async () => {
      try {
        setLoading(true);
        const turmasSnap = await getDocs(collection(db, paths.turmas));
        const turmasMap = {};
        const listaTurmas = [];
        turmasSnap.forEach(doc => {
            const data = doc.data();
            if (data.nome) {
                turmasMap[doc.id] = data.nome;
                listaTurmas.push({ id: doc.id, nome: data.nome });
            }
        });
        listaTurmas.sort((a, b) => a.nome.localeCompare(b.nome));
        setMapaTurmas(turmasMap);
        setTurmasDisponiveis(listaTurmas);

        if (listaTurmas.length > 0) {
            const turmaPreferencial = listaTurmas.find(t => t.nome === "Aspirantes 2027");
            if (turmaPreferencial) {
                setTurmaSelecionadaId(turmaPreferencial.id);
            } else {
                setTurmaSelecionadaId(listaTurmas[0].id);
            }
        }
        
        const q = query(collection(db, paths.users), where("nivel", "==", "cadete"));
        const snapshot = await getDocs(q);
        const listaCadetes = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
                id: doc.id,
                ...data,
                turma_id: data.turma_id ?? null,
                genero: normalizeText(data.genero || data.sexo || "N/I"),
                nascimento: data.data_nascimento || data.nascimento || null,
                vinculo: normalizeText(data.vinculo), 
                origem: normalizeText(data.origem_ingresso || data.forma_ingresso || data.origem || "CIVIL"),
                data_posse: data.data_posse || data.posse || data.posse_pm || data.data_inclusao || null
            };
        });
        setCadetes(listaCadetes);
      } catch (error) {
        console.error("Erro ao carregar dados:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchDados();
  }, []);

  const dadosFiltrados = useMemo(() => {
    if (!turmaSelecionadaId) return [];
    return cadetes.filter(c => c.turma_id === turmaSelecionadaId);
  }, [cadetes, turmaSelecionadaId]);

  const checkIsPM = (cadete) => {
      if (cadete.vinculo === 'CIVIL') return false;
      if (cadete.vinculo.includes('MILITAR') || cadete.vinculo.includes('PM')) return true;
      const terms = ["PM", "MILITAR", "SD", "CB", "SGT", "TEN", "SOLDADO", "CABO", "POLICIA", "AL OF"];
      const hasTerm = terms.some(term => cadete.origem.includes(term));
      const hasPosseDate = !!cadete.data_posse && cadete.data_posse.trim() !== "";
      return hasTerm || hasPosseDate;
  };

  const getIngressoDate = (cadete) => {
      return getYear(cadete.data_posse);
  };

  const dadosGenero = useMemo(() => {
    let masc = 0;
    let fem = 0;
    dadosFiltrados.forEach(c => {
        if (c.genero.startsWith('F') || c.genero.includes('MULHER')) fem++;
        else masc++;
    });
    return [{ name: 'FEM', value: fem }, { name: 'MASC', value: masc }].filter(d => d.value > 0);
  }, [dadosFiltrados]);

  const dadosOrigem = useMemo(() => {
      let pm = 0;
      let civil = 0;
      dadosFiltrados.forEach(c => {
          if (checkIsPM(c)) pm++;
          else civil++;
      });
      return [{ name: 'CIVIL', value: civil }, { name: 'PM', value: pm }].filter(d => d.value > 0);
  }, [dadosFiltrados]);

  const { dadosIdadeGrupos, dadosAnoNascimento } = useMemo(() => {
      const groups = { 'Até 1990': 0, '1991 a 1995': 0, '1996 a 2000': 0, '2001 a 2005': 0, '2006 a 2010': 0, '2011+': 0 };
      const yearsCount = {};
      dadosFiltrados.forEach(c => {
          const ano = getYear(c.nascimento);
          if (ano) {
              yearsCount[ano] = (yearsCount[ano] || 0) + 1;
              if (ano <= 1990) groups['Até 1990']++;
              else if (ano <= 1995) groups['1991 a 1995']++;
              else if (ano <= 2000) groups['1996 a 2000']++;
              else if (ano <= 2005) groups['2001 a 2005']++;
              else if (ano <= 2010) groups['2006 a 2010']++;
              else groups['2011+']++;
          }
      });
      return { 
          dadosIdadeGrupos: Object.keys(groups).map(k => ({ name: k, value: groups[k] })).filter(d => d.value > 0),
          dadosAnoNascimento: Object.keys(yearsCount).sort().map(k => ({ name: k, value: yearsCount[k] }))
      };
  }, [dadosFiltrados]);

  const { dadosTempoServico, dadosAnoIngresso } = useMemo(() => {
      const groups = { 'Até 2010': 0, '2011 a 2015': 0, '2016 a 2020': 0, '2021 a 2025': 0 };
      const yearsCount = {};
      dadosFiltrados.forEach(c => {
          if (checkIsPM(c)) {
              const ano = getIngressoDate(c);
              if (ano) {
                  yearsCount[ano] = (yearsCount[ano] || 0) + 1;
                  if (ano <= 2010) groups['Até 2010']++;
                  else if (ano <= 2015) groups['2011 a 2015']++;
                  else if (ano <= 2020) groups['2016 a 2020']++;
                  else groups['2021 a 2025']++;
              }
          }
      });
      return { 
          dadosTempoServico: Object.keys(groups).map(k => ({ name: k, value: groups[k] })).filter(d => d.value > 0), 
          dadosAnoIngresso: Object.keys(yearsCount).sort().map(k => ({ name: k, value: yearsCount[k] }))
      };
  }, [dadosFiltrados]);

  const ChartCard = ({ title, children, className = "" }) => (
      <div className={`bg-white p-5 rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-300 border border-slate-100 flex flex-col h-full ${className}`}>
          <h3 className="text-left font-semibold text-slate-500 tracking-wide uppercase text-xs mb-4">{title}</h3>
          <div className="flex-grow w-full h-64 relative">
              {children}
          </div>
      </div>
  );

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-4 sm:p-6 bg-slate-50 min-h-screen font-sans">
       
       <div className="mb-8 bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 tracking-tight">Dashboard de Perfil</h1>
            <p className="text-sm text-slate-500 mt-1">Análise do corpo de cadetes</p>
          </div>
          
          <div className="w-full sm:w-auto flex flex-col sm:flex-row items-start sm:items-center gap-3">
             <label className="text-sm font-medium text-slate-500 hidden sm:block">Turma:</label>
             <select 
                value={turmaSelecionadaId} 
                onChange={(e) => setTurmaSelecionadaId(e.target.value)}
                className="w-full sm:w-64 p-2.5 px-4 border border-slate-200 rounded-xl text-sm bg-slate-50 text-slate-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all cursor-pointer font-medium appearance-none"
                style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: `right 0.5rem center`, backgroundRepeat: `no-repeat`, backgroundSize: `1.5em 1.5em` }}
             >
                {turmasDisponiveis.map(t => (
                    <option key={t.id} value={t.id}>{t.nome}</option>
                ))}
             </select>
          </div>
       </div>

       {dadosFiltrados.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-16 bg-white rounded-2xl border border-slate-100 border-dashed text-slate-400">
            <svg className="w-16 h-16 mb-4 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <p className="text-lg font-medium text-slate-600">Nenhum dado encontrado</p>
            <p className="text-sm mt-1">Selecione outra turma no filtro acima.</p>
          </div>
       ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              
              <ChartCard title="Masc / Fem">
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie 
                            data={dadosGenero} 
                            cx="50%" cy="50%" 
                            innerRadius={40} outerRadius={80} 
                            paddingAngle={5}
                            dataKey="value"
                            stroke="none"
                            labelLine={false}
                            label={renderCustomizedLabel}
                        >
                            {dadosGenero.map((entry, index) => (<Cell key={`cell-${index}`} fill={COLORS_GENDER[index % COLORS_GENDER.length]} />))}
                        </Pie>
                        <Legend iconType="circle" layout="horizontal" verticalAlign="bottom" align="center" wrapperStyle={{fontSize: '12px', color: '#64748b', paddingTop: '20px'}} />
                        <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="Faixa Etária (Grupos)">
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie 
                            data={dadosIdadeGrupos} 
                            cx="50%" cy="50%" 
                            innerRadius={40} outerRadius={80} 
                            paddingAngle={3}
                            dataKey="value"
                            stroke="none"
                            labelLine={false}
                            label={renderCustomizedLabel}
                        >
                            {dadosIdadeGrupos.map((entry, index) => (<Cell key={`cell-${index}`} fill={COLORS_AGE_PIE[index % COLORS_AGE_PIE.length]} />))}
                        </Pie>
                        <Legend iconType="circle" layout="vertical" verticalAlign="middle" align="right" wrapperStyle={{fontSize: '11px', color: '#64748b'}} width={100}/>
                        <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="Origem: PM / Civil">
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie 
                            data={dadosOrigem} 
                            cx="50%" cy="50%" 
                            innerRadius={40} outerRadius={80} 
                            paddingAngle={5}
                            dataKey="value"
                            stroke="none"
                            labelLine={false}
                            label={renderCustomizedLabel}
                        >
                            {dadosOrigem.map((entry, index) => (<Cell key={`cell-${index}`} fill={COLORS_ORIGIN[index % COLORS_ORIGIN.length]} />))}
                        </Pie>
                        <Legend iconType="circle" layout="horizontal" verticalAlign="bottom" align="center" wrapperStyle={{fontSize: '12px', color: '#64748b', paddingTop: '20px'}} />
                        <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="Distribuição por Ano de Nasc.">
                 <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dadosAnoNascimento} margin={{ top: 20, right: 10, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                        <XAxis dataKey="name" tick={{fontSize: 11, fill: '#94a3b8'}} tickLine={false} axisLine={{stroke: '#e2e8f0'}} />
                        <YAxis tick={{fontSize: 11, fill: '#94a3b8'}} tickLine={false} axisLine={false} />
                        <Tooltip content={<CustomTooltip />} cursor={{fill: '#f8fafc'}} />
                        <Bar dataKey="value" fill={BAR_COLOR_AGE} radius={[4, 4, 0, 0]}>
                            <LabelList dataKey="value" position="top" style={{ fill: '#475569', fontSize: '11px', fontWeight: '600' }} />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="Tempo de Serviço PM (Grupos)">
                {dadosTempoServico.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie 
                                data={dadosTempoServico} 
                                cx="50%" cy="50%" 
                                innerRadius={40} outerRadius={80} 
                                paddingAngle={3}
                                dataKey="value"
                                stroke="none"
                                labelLine={false}
                                label={renderCustomizedLabel}
                            >
                                {dadosTempoServico.map((entry, index) => (<Cell key={`cell-${index}`} fill={COLORS_AGE_PIE[index % COLORS_AGE_PIE.length]} />))}
                            </Pie>
                            <Legend iconType="circle" layout="vertical" verticalAlign="middle" align="right" wrapperStyle={{fontSize: '11px', color: '#64748b'}} width={100}/>
                            <Tooltip content={<CustomTooltip />} />
                        </PieChart>
                    </ResponsiveContainer>
                ) : (
                    <div className="flex flex-col items-center justify-center h-full text-slate-400 text-sm text-center px-4 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                        <p className="font-medium text-slate-500">Sem dados de Tempo de Serviço</p>
                        <p className="mt-1 text-xs opacity-80">Verifique o campo 'Posse (PM)'</p>
                    </div>
                )}
              </ChartCard>

               <ChartCard title="Ano de Ingresso na PM">
                 {dadosAnoIngresso.length > 0 ? (
                     <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={dadosAnoIngresso} margin={{ top: 20, right: 10, left: -20, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                            <XAxis dataKey="name" tick={{fontSize: 11, fill: '#94a3b8'}} tickLine={false} axisLine={{stroke: '#e2e8f0'}} />
                            <YAxis tick={{fontSize: 11, fill: '#94a3b8'}} tickLine={false} axisLine={false} />
                            <Tooltip content={<CustomTooltip />} cursor={{fill: '#f8fafc'}} />
                            <Bar dataKey="value" fill={BAR_COLOR_INGRESSO} radius={[4, 4, 0, 0]}>
                                <LabelList dataKey="value" position="top" style={{ fill: '#475569', fontSize: '11px', fontWeight: '600' }} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                 ) : (
                    <div className="flex flex-col items-center justify-center h-full text-slate-400 text-sm text-center px-4 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                        <p className="font-medium text-slate-500">Sem dados de Ingresso</p>
                        <p className="mt-1 text-xs opacity-80">Verifique o campo 'Posse (PM)'</p>
                    </div>
                 )}
              </ChartCard>

          </div>
       )}
    </div>
  );
};